import React, { useEffect, useState } from "react";
import { connect, useSelector, useDispatch } from "react-redux";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from "@material-ui/core";
import {
  updateConsumerPageSetting,
  getConsumerPageSetting,
  resetSetting,
  fetchJSONURL,
} from "../../../redux";
import { selectLoading } from "modules/notification";
import ConsumerPageSettingPanelComponent from "./consumerPageSettingPanel.component";
import { SkeletonSettingComponent } from "../../skeleton/skeleton.component";
import { AccessRights } from "lib/constants/accessRights";
import { addAlert } from "modules/notification";
import { getUploadS3FilePath } from "lib/helper";
import uploadImage from "../../../utils/uploadImage.util";
import { FolderType, UploadType } from "lib/constants/aws_s3";
import { getLang } from "app/feature/constants";
import languageType from "lib/constants/languages";
import { uploadJSONToS3 } from "app/feature";
import { unwrapResult } from "@reduxjs/toolkit";
import { setImgFormatAndSize } from "../../../../../lib/FileHelper";
import { canAccess } from "../../../../profile/redux/selector/profile.selector";
import ConfirmationDialogComponent from "components/dialog/confirmation/confirmationDialog.component";

function ConsumerPageSettingPanelContainer({
  settings,
  updateConsumerPageSetting,
  getConsumerPageSetting,
  isFetching,
  isChanging,
  resetSetting,
}) {
  const dispatch = useDispatch();
  const lang = useSelector((state) => state.constant.languages);
  const hasCompanyProductEditAccessRight = useSelector((state) =>
    canAccess(state, AccessRights.Company.PRODUCT_PAGE_WRITE)
  );

  const canEditGeneralSetting = useSelector((state) =>
    canAccess(state, AccessRights.Company.PRODUCT_PAGE_WRITE_GENERAL)
  );
  const canEditDefaultProfileSetting = useSelector((state) =>
    canAccess(state, AccessRights.Company.DEFAULT_PROFILE)
  );
  const canEditContact = useSelector((state) =>
    canAccess(state, AccessRights.Company.PRODUCT_PAGE_WRITE_CONTACT)
  );
  const canEditLanguage = useSelector((state) =>
    canAccess(state, AccessRights.Company.PRODUCT_PAGE_WRITE_LANGUAGE)
  );
  const canEditSocialMedia = useSelector((state) =>
    canAccess(state, AccessRights.Company.PRODUCT_PAGE_WRITE_SOCIAL_MEDIA)
  );
  const canEditPrivacyPolicy = useSelector((state) =>
    canAccess(state, AccessRights.Company.PRODUCT_PAGE_WRITE_PRIVACY_POLICY)
  );

  const isLoading = useSelector((state) =>
    selectLoading(state, fetchJSONURL.typePrefix)
  );
  const uiSetting = useSelector((state) => state.setting.uiSetting);
  const companyId = useSelector((state) => state.account.id);
  const id = useSelector((state) => state.session.idTokenPayload.sub);
  const [showNotification, setShowNotification] = useState({ open: false });
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    onConfirm: null,
  });

  const handleSocialMediaToggle = (
    fieldName,
    fieldLabel,
    currentValue,
    formik
  ) => {
    if (currentValue) {
      // If turning off, show confirmation
      setConfirmDialog({
        open: true,
        title: getLang(lang, "label.CONFIRMATION_OF_DISABLE_OPTIONS", {
          login: fieldLabel,
        }),
        onConfirm: () => {
          formik.setFieldValue(fieldName, false);
          setConfirmDialog({ open: false, title: "", onConfirm: null });
        },
      });
    } else {
      // If turning on, no confirmation needed
      formik.setFieldValue(fieldName, true);
    }
  };
  const tncUploadToS3 = async (values) => {
    let url = "";
    if (values.privacyPolicyMessage) {
      const fileName = "JsonFile";

      const uploadJSONdata = {
        uploadType: UploadType.privacyPolicyTermAndCondition,
        data: values.privacyPolicyMessage,
        id: id,
        fileName: `${Date.now()}${fileName}`,
      };
      await dispatch(uploadJSONToS3(uploadJSONdata))
        .then(unwrapResult)
        .then((jsonUrl) => {
          url = jsonUrl;
        })
        .catch((error) => {
          dispatch(
            addAlert({
              severity: "error",
              message: error.message || "Upload Terms & Conditions Failed",
            })
          );
        });
      return url;
    }
  };

  const handleSubmit = async (values) => {
    let file = uiSetting.bgImage;
    let bannerImageFile = uiSetting.bannerImage;
    let backgroundUrl = "";
    let bannerImageUrl = "";
    let tncUrl = "";
    let isPrivacyPolicyMessageModified = "0";
    let privacyPolicyMessage = values.privacyPolicyMessage;
    if (
      values.enablePrivacyPolicy &&
      values.privacyPolicyMessage &&
      values.privacyPolicyMessage.value === "<p></p>\n"
    ) {
      dispatch(
        addAlert({
          severity: "error",
          message: getLang(lang, "message.error.EMPTY_TNC"),
        })
      );
      return;
    }

    if (uiSetting.bgType === "Image" && uiSetting.bgImage === "") {
      dispatch(
        addAlert({
          severity: "error",
          message: getLang(lang, "message.error.ERROR_UPLOAD_IMAGES"),
        })
      );
    }

    if (
      values.privacyPolicyMessage?.value !==
      settings.privacyPolicyMessage?.value
    )
      isPrivacyPolicyMessageModified = "1";

    if (values.privacyPolicyMessage.value !== "<p></p>\n") {
      tncUrl = await tncUploadToS3(values);
      if (tncUrl?.length > 0) {
        values.privacyPolicyMessage = tncUrl.split("?")[0];
      }
    } else {
      values.privacyPolicyMessage = "";
    }

    if (!file.includes("https") && uiSetting.bgType === "Image") {
      const fileName = `consumerPageBg${companyId}${Date.now()}.webp`;
      const filePath = getUploadS3FilePath({
        type: FolderType.backgroundImageSetting,
        id: companyId,
        fileName: fileName,
      });

      file = await setImgFormatAndSize(file, 1080, 1920, "image/webp");

      const responseS3 = await uploadImage(filePath, file);

      if (responseS3.success) {
        backgroundUrl = `${responseS3.url.split("?")[0]}`;
      } else {
        dispatch(
          addAlert({
            severity: "error",
            message: getLang(lang, "message.error.ERROR_UPLOAD_IMAGES"),
          })
        );
        return;
      }
    }

    if (bannerImageFile !== "" && !bannerImageFile.includes("https")) {
      const fileName = `consumerPageBanner${companyId}${Date.now()}.webp`;
      const filePath = getUploadS3FilePath({
        type: FolderType.consumerBanner,
        id: companyId,
        fileName: fileName,
      });

      bannerImageFile = await setImgFormatAndSize(
        bannerImageFile,
        3132,
        1080,
        "image/webp"
      );

      const responseS3 = await uploadImage(filePath, bannerImageFile);

      if (responseS3.success) {
        bannerImageUrl = `${responseS3.url.split("?")[0]}`;
      } else {
        dispatch(
          addAlert({
            severity: "error",
            message: getLang(lang, "message.error.ERROR_UPLOAD_IMAGES"),
          })
        );
        return;
      }
    }

    const payload = {
      ...values,
      defaultLanguage: languageType[values.defaultLanguage].code,
      uiSetting: {
        bgType: uiSetting.bgType,
        bgImage: file.includes("https") ? uiSetting.bgImage : backgroundUrl,
        bannerImage:
          bannerImageFile === ""
            ? ""
            : bannerImageFile.includes("https")
            ? uiSetting.bannerImage
            : bannerImageUrl,
        bgColor: uiSetting.bgColor,
        colorPrimary: uiSetting.colorPrimary,
        colorSecondary: uiSetting.colorSecondary,
      },
      isPrivacyPolicyMessageModified: isPrivacyPolicyMessageModified,
    };

    updateConsumerPageSetting(payload);
    values.privacyPolicyMessage = privacyPolicyMessage;
  };

  useEffect(() => {
    getConsumerPageSetting();
    return () => {
      resetSetting();
    };
  }, [getConsumerPageSetting, resetSetting]);

  if (isFetching) return <SkeletonSettingComponent />;

  return (
    <>
      <ConsumerPageSettingPanelComponent
        hasCompanyEditAccessRight={hasCompanyProductEditAccessRight}
        canEditGeneralSetting={canEditGeneralSetting}
        canEditDefaultProfileSetting={canEditDefaultProfileSetting}
        canEditContact={canEditContact}
        canEditLanguage={canEditLanguage}
        canEditSocialMedia={canEditSocialMedia}
        canEditPrivacyPolicy={canEditPrivacyPolicy}
        handleSubmit={handleSubmit}
        settings={settings}
        isChanging={isChanging}
        lang={lang}
        isLoading={isLoading}
        setShowNotification={setShowNotification}
        handleSocialMediaToggle={handleSocialMediaToggle}
      />

      {/* Social Media Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() =>
          setConfirmDialog({ open: false, title: "", onConfirm: null })
        }
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{getLang(lang, "label.DISABLE_CONFIRMATION")}</DialogTitle>
        <DialogContent>
          <Typography color="error" style={{ marginBottom: 16 }}>
            {getLang(lang, "label.WARNING_OF_DISABLE")}
          </Typography>
          <Typography style={{ marginTop: 16 }}>{confirmDialog.title}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() =>
              setConfirmDialog({ open: false, title: "", onConfirm: null })
            }
            color="primary"
          >
            {getLang(lang, "label.NO")}
          </Button>
          <Button
            onClick={confirmDialog.onConfirm}
            color="primary"
            variant="contained"
          >
            {getLang(lang, "label.YES")}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmationDialogComponent
        isOpen={showNotification.open}
        handleClose={() => setShowNotification({ open: false })}
        handleProceed={
          showNotification.onConfirm ||
          (() => setShowNotification({ open: false }))
        }
        title={getLang(lang, "label.WARNING")}
        type={"danger"}
        description={
          <>
            <p>{getLang(lang, "message.info.FEATURES_NOT_AVAILABLE")}</p>
            <br />
            <p>{getLang(lang, "message.info.WARRANTY_SETTINGS_UPDATED")}</p>
          </>
        }
      />
    </>
  );
}

const mapStateToProps = (state) => ({
  settings: state.setting,
  isChanging: selectLoading(state, updateConsumerPageSetting.typePrefix),
  isFetching: selectLoading(state, getConsumerPageSetting.typePrefix),
});

const mapDispatchToProps = (dispatch) => ({
  updateConsumerPageSetting: (values) =>
    dispatch(updateConsumerPageSetting(values)),
  getConsumerPageSetting: () => dispatch(getConsumerPageSetting()),
  resetSetting: () => dispatch(resetSetting()),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ConsumerPageSettingPanelContainer);
